import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { images } from '../utils/images';
import {
  CheckCircle2,
  Users,
  Target,
  Heart,
  Phone,
  Shield,
  Award,
  MapPin,
  Compass,
  HandHeart,
  ClipboardList,
  Briefcase
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StickyCallButton from '../components/StickyCallButton';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import VideoBackground from '../components/VideoBackground';

const WhatIsNdisSupportProviderPage = () => {
  const whatTheyDo = [
    {
      icon: Compass,
      title: "Deliver the Supports in Your Plan",
      description: "An NDIS support provider is the organisation or person who actually delivers the services funded in your NDIS plan. Whether it's personal care, therapy, transport, or community access, they're the ones doing the hands-on work."
    },
    {
      icon: Users,
      title: "Help You Live More Independently",
      description: "From daily living assistance to building life skills, a good provider helps you gain confidence and independence, one step at a time. They work with you, not just for you."
    },
    {
      icon: Target,
      title: "Support You in Reaching Your Goals",
      description: "Your NDIS plan is built around your goals. A quality provider tailors their services to help you achieve them, whether that's finding a job, joining community activities, or managing your health."
    },
    {
      icon: Shield,
      title: "Keep You Safe and Supported",
      description: "NDIS providers meet strict quality and safety standards set by the NDIS Quality and Safeguards Commission. This means you can trust that the support you receive is professional, respectful, and accountable."
    },
    {
      icon: ClipboardList,
      title: "Work with Your Plan and Your Team",
      description: "A great provider doesn't work in isolation. They collaborate with your support coordinator, your family, and your other providers to make sure everything runs smoothly and your plan is used effectively."
    },
    {
      icon: HandHeart,
      title: "Treat You Like a Person, Not a Number",
      description: "The best providers genuinely care. They take time to understand you, respect your choices, and adjust their approach as your needs change. That's what real support looks like."
    }
  ];

  const whoCanBenefit = [
    {
      title: "People New to the NDIS",
      description: "If you've just been approved and need help finding the right services, understanding what a provider does is the first step to getting the support you deserve."
    },
    {
      title: "Participants with Complex Needs",
      description: "If you need multiple types of support across different areas of your life, the right provider can bring it all together under one roof."
    },
    {
      title: "Families and Carers",
      description: "If you're supporting a loved one, a reliable provider takes the weight off your shoulders and gives you peace of mind that they're in good hands."
    },
    {
      title: "People Looking to Change Providers",
      description: "Not happy with your current provider? You have every right to switch. Finding a provider who truly listens can make all the difference."
    },
    {
      title: "Participants Wanting More from Their Plan",
      description: "If you feel your current supports aren't helping you progress, a better-matched provider can unlock the full potential of your NDIS plan."
    },
    {
      title: "Anyone Who Wants Genuine, Caring Support",
      description: "At the end of the day, everyone deserves support from people who actually care. That's what a good NDIS provider looks like."
    }
  ];

  const typesOfSupport = [
    {
      level: "Daily Living",
      title: "Personal Care & Daily Support",
      description: "Help with everyday tasks like personal hygiene, meals, household chores, and getting around. Your provider makes daily life easier so you can focus on the things that matter to you.",
      colour: "#81b956"
    },
    {
      level: "Therapies",
      title: "Allied Health & Therapies",
      description: "Access to physiotherapy, occupational therapy, speech therapy, psychology, and more. These services help you build skills, manage your health, and improve your quality of life.",
      colour: "#587740"
    },
    {
      level: "Community",
      title: "Community & Social Participation",
      description: "Support to get involved in community activities, recreation, social groups, and employment. A good provider helps you build real connections and live a full, active life.",
      colour: "#2d6142"
    }
  ];

  const whyChooseUs = [
    {
      icon: Heart,
      title: "We Genuinely Care",
      description: "We don't treat you like a file number. We listen, we understand, and we walk beside you every step of the way."
    },
    {
      icon: Award,
      title: "Registered and Trusted",
      description: "Life Care and Wellness is an NDIS provider. We meet all quality and safety standards, so you can have confidence in the support you receive."
    },
    {
      icon: CheckCircle2,
      title: "Plain Language, No Jargon",
      description: "We explain everything in simple terms. You'll always know what's happening and why."
    },
    {
      icon: MapPin,
      title: "Local to Brisbane and Logan",
      description: "We're based in Loganholme, Queensland, and we offer both in-person and remote support anywhere in Australia."
    },
    {
      icon: Users,
      title: "Culturally Sensitive and Multilingual",
      description: "We support people from all backgrounds and offer services in multiple languages."
    },
    {
      icon: Shield,
      title: "We Fight for You",
      description: "When things aren't right, we don't stay quiet. We advocate, we push, and we make sure your voice is heard."
    }
  ];

  const faqs = [
    {
      question: "What is an NDIS support provider?",
      answer: "An NDIS support provider is an organisation or individual that delivers disability support services funded through the NDIS. This can include everything from personal care and therapy to community access, employment support, and more."
    },
    {
      question: "What's the difference between a large and small provider?",
      answer: "A large NDIS provider typically offers a wide range of services across many locations, but you may feel like just another number. A smaller provider like Life Care and Wellness offers more personalised, hands-on support where you're treated as an individual, not a case file."
    },
    {
      question: "How is a support provider different from a support coordinator?",
      answer: "A support provider delivers the actual services in your plan, like personal care, therapy, or community access. A support coordinator helps you understand your plan, find providers, and manage the bigger picture. Think of the coordinator as the planner and the provider as the doer."
    },
    {
      question: "Can I choose my own NDIS provider?",
      answer: "Absolutely. You have complete choice and control over who provides your supports. You can choose any provider that feels right for you, and you can change providers at any time if you're not happy."
    },
    {
      question: "What services does Life Care and Wellness provide?",
      answer: "We offer a wide range of NDIS services including support coordination, daily living assistance, community access, mental health support, counselling, employment support, recreation programs, and more. We tailor everything to your individual needs and goals."
    },
    {
      question: "How do I switch to a new provider?",
      answer: "Switching providers is your right. Simply let your current provider know, and reach out to us. We'll help make the transition smooth and stress-free. You don't need anyone's permission to change."
    },
    {
      question: "Do I need a referral to use your services?",
      answer: "No referral is needed. If you have an NDIS plan with relevant funding, you can contact us directly. We also offer free consultations to help you understand your options before committing to anything."
    },
    {
      question: "Can you help if I don't have an NDIS plan yet?",
      answer: "Yes. We can guide you through the application process, help you understand eligibility, and support you in putting together a strong application. We also have a dedicated 'Get on the NDIS Free' service to help you get started at no cost."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="What is an NDIS Support Provider?"
        description="Learn what an NDIS Support Provider does, the types of services they deliver, and how to choose the right provider for your needs. Free consultation available."
      />
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            <div>
              <h1
                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
                style={{ color: '#587740' }}
                data-testid="support-provider-hero-title"
              >
                What is an NDIS Support Provider?
              </h1>
              <p
                className="text-lg md:text-xl leading-relaxed mb-4"
                style={{ color: '#5e5e5e' }}
              >
                An NDIS support provider is the organisation or person who delivers the services and supports funded in your NDIS plan. They're the people who show up, do the work, and help you live the life you choose.
              </p>
              <p
                className="text-lg leading-relaxed mb-4"
                style={{ color: '#5e5e5e' }}
              >
                But not all providers are the same. The right provider doesn't just deliver a service and move on. They take the time to understand you, your goals, and your challenges. They communicate clearly, treat you with respect, and genuinely care about your progress.
              </p>
              <p
                className="text-base leading-relaxed italic mb-4"
                style={{ color: '#6c935e' }}
              >
                You deserve a provider who sees you as a person, not a plan number. That's what we believe at Life Care and Wellness.
              </p>
              <p
                className="text-sm font-semibold mb-6"
                style={{ color: '#587740' }}
              >
                Book a free consultation and we'll help you understand your options at no cost.
              </p>
              <Button
                onClick={() => window.location.href = '/contact'}
                size="lg"
                className="text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#81b956' }}
                data-testid="support-provider-hero-cta"
              >
                <Phone className="w-5 h-5 mr-2" />
                Contact Us
              </Button>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={images.supportCoordinator1}
                alt="NDIS Support Provider helping a participant"
                className="w-full h-[400px] md:h-[500px] object-cover"
                data-testid="support-provider-hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What Does a Support Provider Do Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
              style={{ color: '#587740' }}
            >
              What Does an NDIS Support Provider Do?
            </h2>
            <p
              className="text-lg md:text-xl"
              style={{ color: '#5e5e5e' }}
            >
              In plain language, they're the people who turn your NDIS plan into real, everyday support.
              Here's what a good provider does:
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {whatTheyDo.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card
                  key={index}
                  className="border-none shadow-lg hover:shadow-xl transition-all duration-300"
                  data-testid={`what-they-do-card-${index}`}
                >
                  <CardContent className="p-8">
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
                      style={{ backgroundColor: '#AABC5A' }}
                    >
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3
                      className="text-xl font-bold mb-4"
                      style={{ color: '#587740' }}
                    >
                      {item.title}
                    </h3>
                    <p
                      className="text-base leading-relaxed"
                      style={{ color: '#5e5e5e' }}
                    >
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Types of Support Section */}
      <section className="py-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Types of Support a Provider Can Deliver
              </h2>
              <p
                className="text-lg md:text-xl"
                style={{ color: '#5e5e5e' }}
              >
                NDIS providers can offer a wide range of services depending on what's in your plan:
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {typesOfSupport.map((type, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                  data-testid={`support-type-${index}`}
                >
                  <div
                    className="p-4 text-center"
                    style={{ backgroundColor: type.colour }}
                  >
                    <span className="text-white text-sm font-semibold uppercase tracking-wider">
                      {type.level}
                    </span>
                    <h3 className="text-xl font-bold text-white mt-1">
                      {type.title}
                    </h3>
                  </div>
                  <div className="p-6">
                    <p
                      className="text-base leading-relaxed"
                      style={{ color: '#5e5e5e' }}
                    >
                      {type.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Who Can Benefit Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <h2
                  className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                  style={{ color: '#587740' }}
                >
                  Who Can Benefit from an NDIS Support Provider?
                </h2>
                <p
                  className="text-lg leading-relaxed mb-4"
                  style={{ color: '#5e5e5e' }}
                >
                  If you have an NDIS plan, you need a provider to deliver the supports in it.
                  But choosing the right one makes all the difference.
                </p>
                <p
                  className="text-base leading-relaxed italic"
                  style={{ color: '#6c935e' }}
                >
                  You don't have to settle for a provider who doesn't listen. The right provider
                  changes everything.
                </p>
              </div>
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={images.supportCoordinator2}
                  alt="Free NDIS consultation at Life Care and Wellness"
                  className="w-full h-[400px] object-cover"
                  data-testid="who-can-benefit-image"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {whoCanBenefit.map((item, index) => (
                <Card
                  key={index}
                  className="border-none shadow-md hover:shadow-lg transition-shadow"
                >
                  <CardContent className="p-6">
                    <CheckCircle2
                      className="w-8 h-8 mb-4"
                      style={{ color: '#81b956' }}
                    />
                    <h3
                      className="text-lg font-bold mb-3"
                      style={{ color: '#587740' }}
                    >
                      {item.title}
                    </h3>
                    <p
                      className="text-base leading-relaxed"
                      style={{ color: '#5e5e5e' }}
                    >
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Life Care and Wellness */}
      <section className="py-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Why Choose Life Care and Wellness as Your Provider?
              </h2>
              <p
                className="text-lg md:text-xl"
                style={{ color: '#5e5e5e' }}
              >
                Because you deserve more than a provider who just ticks boxes.
                Here's what makes us different:
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                {whyChooseUs.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-start space-x-4">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: index % 2 === 0 ? '#AABC5A' : '#81b956' }}
                      >
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3
                          className="text-xl font-bold mb-2"
                          style={{ color: '#587740' }}
                        >
                          {item.title}
                        </h3>
                        <p
                          className="text-base leading-relaxed"
                          style={{ color: '#5e5e5e' }}
                        >
                          {item.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={images.supportCoordinator3}
                  alt="Life Care and Wellness office"
                  className="w-full h-full object-cover"
                  data-testid="why-choose-us-image"
                />
              </div>
            </div>

            <div className="text-center mt-12 p-8 rounded-2xl bg-white shadow-md">
              <p
                className="text-2xl font-bold"
                style={{ color: '#587740' }}
              >
                At Life Care and Wellness, we're not just a provider. We're your partners in building the life you want.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Frequently Asked Questions
              </h2>
              <p
                className="text-lg"
                style={{ color: '#5e5e5e' }}
              >
                Got questions? We've got answers. And if you can't find what you're looking for,
                reach out to us at admin@lifecarewellness.au
              </p>
            </div>

            <Accordion type="single" collapsible className="w-full space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="border rounded-lg px-6 shadow-sm"
                >
                  <AccordionTrigger
                    className="text-left hover:no-underline py-6"
                    style={{ color: '#587740' }}
                  >
                    <span className="text-lg font-semibold pr-4">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-base leading-relaxed pb-6"
                    style={{ color: '#5e5e5e' }}
                  >
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <VideoBackground variant="dark" className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Ready to Find the Right NDIS Provider?
            </h2>
            <p className="text-lg md:text-xl mb-8 text-white/90">
              The right provider changes everything. Free consultation, free action plan, and genuine
              support from people who truly care. Let us show you what real NDIS support looks like.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => window.location.href = '/contact'}
                size="lg"
                className="text-base px-8 py-6"
                style={{ backgroundColor: '#FFFFFF', color: '#587740' }}
                data-testid="support-provider-cta-contact"
              >
                <Phone className="w-5 h-5 mr-2" />
                Get Your Free Consultation
              </Button>
              <a href="/get-on-ndis-free">
                <Button
                  size="lg"
                  variant="outline"
                  className="text-base px-8 py-6 border-2 border-white text-white hover:bg-white/10"
                  data-testid="support-provider-cta-ndis-free"
                >
                  Get on the NDIS Free
                </Button>
              </a>
            </div>
          </div>
        </div>
      </VideoBackground>

      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default WhatIsNdisSupportProviderPage;
